/**
 * ONE RUN's rows, as it happens and afterwards.
 *
 * Not to be confused with `core/feed.ts`, which is the EXPLORATION's feed — every
 * pass, every question, every run marker, on one epoch-millisecond axis, for the
 * thread's column. This module answers a narrower question for the Runs tab: what
 * did this run do, in the order it did it, and what did it come to. The two are
 * kept apart because their rows are not the same kind of thing — a row here is a
 * logical MOVE inside one run, numbered by `RunMove.at`, and a row there is an
 * event in a session, stamped in milliseconds. Merging them would put a move
 * ordinal and a timestamp back on one axis, which is the exact mistake the header
 * below records this file already having made once.
 *
 * THE WHOLE MODULE RESTS ON ONE FACT ABOUT THE RECORD: it is already live.
 * `engine/run.ts` calls `patchRun(id, { moves: … })` after every move, so a run's
 * rows accumulate in `session.runs` while it works. Arrival is therefore a
 * READING of something that was always being written — no projection, no second
 * store of what a run is doing, and nothing at all recomputed from the graph.
 *
 * That last clause is `RunReport`'s rule inherited rather than restated, and it
 * is load-bearing: "a node pruned or re-checked after the fact must not be able
 * to rewrite what the run says it did." A feed assembled from the live graph
 * would let a run's own history change under a reader tidying up afterwards.
 * Because both come off the record, the live feed and the finished account are
 * THE SAME VIEW of the same rows — they differ only in whether `finishedAt` is
 * set — so there is no second description of a run to drift out of step with the
 * first. This codebase keeps finding that failure in itself; see D45.
 *
 * Pure and a LEAF, like `thread.ts`, `cues.ts`, `promote.ts` and `feed.ts`: it
 * imports `./types` and nothing else, so the store can import it without joining
 * a cycle.
 *
 * It also holds no copy of what a move MEANS. `MOVE_REASON` is the one table of
 * those sentences and `kind` stays the raw verb, because a feed that paraphrased
 * either would be a second place for the run's own words to live.
 */

import type { Op, Run, RunMove, RunStop } from './types'

/**
 * One row a feed surface renders.
 *
 * A flat list rather than runs-containing-moves, because the surface scrolls as
 * one column and a nested shape would push the flattening into the renderer —
 * where the ordering rule would then be re-derived per component. `runId` is on
 * every item so a renderer can group without the structure having to.
 *
 * THERE IS NO `at` ON THESE ROWS, deliberately, and the first draft of this file
 * had one that was wrong. `RunMove.at` is a MOVE NUMBER — `l.moves.length + 1`,
 * one-based — while `Run.startedAt`, `Outcome.at` and `Audit.at` in the same type
 * file are epoch milliseconds. A single `at` here therefore carried two units
 * under one name, and any renderer sorting or formatting by it would have been
 * right for one row kind and wrong for the other. Nothing needs the unified axis
 * anyway: runs are ordered by their place in `session.runs` and moves by their
 * place inside a run, and the feed never interleaves two runs.
 *
 * The three kinds are the three things a run says, in the order it says them: it
 * started, it did this, it came to that. `stop` on the foot is nullable because
 * the third sentence has two forms — a run that ended and said why, and a run
 * whose tab died, which has an account but no ending. Both are over; only one of
 * them stopped.
 */
export type RunRow =
  | { runId: string; kind: 'head'; run: Run }
  | { runId: string; kind: 'move'; move: RunMove; ordinal: number }
  | { runId: string; kind: 'foot'; run: Run; stop: RunStop | null }

/**
 * Whether this run is still going, as far as the RECORD is concerned.
 *
 * Not "is the store busy" and not "is there a live run in the store": a tab that
 * died mid-run leaves a record with no `finishedAt` forever, and that absence IS
 * the information (`Run.finishedAt` says so). A feed that asked the store would
 * call such a run live again every time the document was reopened.
 */
export function isUnfinished(run: Run): boolean {
  return run.finishedAt === undefined || run.stop === undefined
}

/**
 * The three states a run can be in, which is one more than the record can tell
 * you on its own.
 *
 * `isUnfinished` answers a question about the RECORD and covers two situations
 * that read identically on disk: a run happening right now, and a run whose tab
 * died mid-flight. The difference is not in the document — it is whether THIS
 * tab is the one running it — so it takes the live run's id as an argument
 * rather than reaching for a store this leaf must not import.
 *
 * It matters because the two want opposite sentences. `RunReport` has always
 * printed "the page closed while it was going" for an unfinished run, which was
 * nearly always true when the only way to read this surface was after the fact.
 * A feed makes mid-run the ordinary case, and that sentence then tells a reader
 * their live run is dead while they watch it work.
 */
export type RunStatus = 'running' | 'abandoned' | 'ended'

export function statusOf(run: Run, liveRunId: string | null): RunStatus {
  if (!isUnfinished(run)) return 'ended'
  return run.id === liveRunId ? 'running' : 'abandoned'
}

/**
 * Every run in the document, oldest first, each expanded into its rows.
 *
 * THE DIRECTION IS THE SAME AT BOTH LEVELS, and the first draft got that wrong
 * in a way only the browser showed. Runs descended while moves ascended, so the
 * newest move of the newest run landed in the MIDDLE of the column — under the
 * first card's account, above every older run — and a surface following its own
 * foot was pinned to the last move of the OLDEST run, a place where nothing
 * arrives and nothing ever will. Two directions in one scrolling column is the
 * same class of mistake as two units under one `at`, and it is caught the same
 * way: pick one and let every level obey it.
 *
 * Chronological is the one to pick, because within a run it is not a choice. A
 * run is an argument that went somewhere, and reversing its steps would make the
 * account read backwards from its own conclusion. That fixes moves ascending,
 * which fixes runs ascending, which puts arrival at the foot of the column —
 * where a reader can be carried along by one rule instead of hunting for the
 * live end among finished ones.
 *
 * It costs the thing the old order was for: the newest account is no longer the
 * first thing under the eye. What buys it back is `foot` — a run's account is an
 * ENDING and is printed at its end, so the bottom of the column is the newest
 * run's account whether it just finished or finished last week. Opening the tab
 * lands there. The old order put the account above the moves, which meant the
 * account of a run still working was printed before the run had one.
 *
 * `liveRunId` is the same argument `statusOf` takes, for the same reason: a foot
 * is what a run came to, and the run happening now has not come to anything yet.
 * Its numbers are still moving, and `RunPod` is the instrument for those.
 */
export function runRowsFor(runs: readonly Run[], liveRunId: string | null = null): RunRow[] {
  const out: RunRow[] = []
  for (const run of runs) {
    out.push({ runId: run.id, kind: 'head', run })
    for (const move of run.moves) {
      // The RECORD's own number, not the array index. They agree by construction
      // — `at` is assigned as `moves.length + 1` at push time — and taking it
      // from the record means a row still says which move it was if one is ever
      // dropped or a partial record is read back off disk.
      out.push({ runId: run.id, kind: 'move', move, ordinal: move.at })
    }
    if (statusOf(run, liveRunId) === 'running') continue
    // Both fields, not just one: a record carrying a finish time without an
    // ending cannot say why it ended, so it gets the same `null` an abandoned run
    // gets. The account is still printed — those counts were patched in as the
    // run went and are the last true thing it recorded — but nothing invents a
    // stop that was never written.
    const ended = run.finishedAt !== undefined && run.stop !== undefined
    out.push({ runId: run.id, kind: 'foot', run, stop: ended ? (run.stop ?? null) : null })
  }
  return out
}

/**
 * The run that produced a node, and the move that produced it — or null for a
 * node no run's op owns.
 *
 * THE JOIN IS BY OP ID, and that is the whole reason `RunMove` carries one while
 * `Op` carries no run field: a run id written inside `beginOp` would tag the
 * user's own keypresses during the gaps between a run's passes as autonomous
 * moves. The direction of the pointer is the invariant, not an implementation
 * detail, so the lookup that follows it lives here rather than being re-derived
 * per component — it was written out twice before this, once in `NodeCard` and
 * once in `Trace`, and the two disagreed about which runs to search.
 *
 * Both halves come back together because every caller needs both: the move is
 * what the run did here, and the run is what a reader is sent to. Two lookups
 * would also be two scans of the same array.
 *
 * BUT AN OP ID IS NOT ENOUGH ON ITS OWN, which is what the node id is for and why
 * this does not take a bare one. A portfolio pass is ONE physical request owning
 * SEVERAL logical moves: `run.ts` pushes one move per part, each with its own
 * verb and its own `at`, all of them under the pass's single op id. So the first
 * move under an op id is the FIRST PART's, and a node made by the second or third
 * part was being given the first part's number, verb and reason. That was wrong
 * in the disclosure this line came from, and wronger on the card, where it is
 * read without opening anything.
 *
 * `PortfolioPart.produced` settles it: the parts say which nodes each verb made,
 * and the moves are pushed in part order, so the k-th move under the op id
 * belongs to `parts[k]`.
 *
 * `feed.ts` makes the same join and takes the FIRST row on purpose, which is not
 * a disagreement: its item is a PASS, and one pass is one op, whose reason is the
 * first part's by construction (`why: i === 0 ? move.why : 'in-progress'`). The
 * item here is a NODE, and a node belongs to one PART. Two questions, two
 * answers, both right — which is why they are two functions and not one with a
 * flag.
 *
 * A `move` of null means the run is known and the move is not — a portfolio whose
 * parts do not place this node. The run still comes back, because a reader can
 * still be sent to it; naming a move that did not produce this node would be a
 * false sentence, and there is no true one to print in its place.
 *
 * The FIRST run holding the op, scanning in stored order. An op id belongs to one
 * op and an op to one run, so a second match is a hand-edited document; taking
 * the earliest makes the answer stable rather than dependent on how many copies
 * were pasted in after it.
 */
export function runMoveForNode(
  runs: readonly Run[],
  op: Op,
  nodeId: string,
): { run: Run; move: RunMove | null } | null {
  for (const run of runs) {
    const mine = run.moves.filter((m) => m.opId === op.id)
    const first = mine[0]
    if (first === undefined) continue
    // Every other kind of pass records exactly one move, and that move is the
    // answer. Only a portfolio owns more than one.
    if (op.portfolio === undefined) return { run, move: first }
    const k = op.portfolio.parts.findIndex((p) => p.produced.includes(nodeId))
    return { run, move: k >= 0 ? (mine[k] ?? null) : null }
  }
  return null
}

/**
 * The run a feed should be watching, or null when none is.
 *
 * The LAST unfinished run rather than the first, because `beginRun` appends and
 * a document can hold several abandoned ones from tabs that died. Only the last
 * can be the one running now.
 */
export function watchingRun(runs: readonly Run[]): Run | null {
  for (let i = runs.length - 1; i >= 0; i--) {
    const run = runs[i]
    if (run !== undefined && isUnfinished(run)) return run
  }
  return null
}
